package dev.lingvo.shared.screenshots.geom

import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.doc.ShapeKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.frames.FrameSpecs
import dev.lingvo.shared.screenshots.frames.VectorFrames
import dev.lingvo.shared.screenshots.render.Affine
import dev.lingvo.shared.screenshots.render.Paths
import kotlin.math.hypot
import kotlin.math.max
import kotlin.math.min

/**
 * Resolves reverse paint order in strip coordinates, excluding entire hidden or locked subtrees.
 * [deviceScreenAspect] should resolve the capture's h/w; without images the layer box supplies the editor fallback.
 * Tolerance expands strokes only, so an adjacent box never steals an outside click.
 */
fun hitTest(
    layers: List<Layer>,
    p: Point,
    tolerance: Double,
    deviceScreenAspect: (DeviceLayer) -> Double = { it.base.box.h / it.base.box.w },
): String? {
    require(tolerance.isFinite() && tolerance >= 0) { "Hit tolerance must be finite and nonnegative" }
    if (!p.x.isFinite() || !p.y.isFinite()) return null
    fun visit(children: List<Layer>, parent: Affine): String? {
        for (layer in children.asReversed()) {
            val base = layer.base
            if (base.hidden || base.locked) continue
            val world = parent * base.box.toParent()
            val local = world.inverse()?.apply(p) ?: continue
            val box = Rect(0.0, 0.0, base.box.w, base.box.h)
            if (layer is GroupLayer) {
                if (layer.clip && !roundedContains(box, layer.cornerRadius, 0.0, local)) continue
                visit(layer.children, world)?.let { return it }
            }
            val inside = when (layer) {
                is DeviceLayer -> {
                    if (box.w <= 0 || box.h <= 0) false else {
                        val layout = VectorFrames.layout(layer.frame, box, deviceScreenAspect(layer), layer.screenRadius)
                        if (layer.frame == FrameKind.WATCH_ROUND) ellipseContains(layout.outer, local)
                        else roundedContains(layout.outer, layout.outerRadius, FrameSpecs.of(layer.frame).smoothing, local)
                    }
                }
                is ShapeLayer -> when (layer.shape) {
                    ShapeKind.RECT -> box.containsPoint(local)
                    ShapeKind.ELLIPSE -> ellipseContains(box, local)
                    ShapeKind.LINE, ShapeKind.CURVE -> {
                        val points = if (layer.shape == ShapeKind.LINE) listOf(Point(0.0, 0.0), Point(box.w, box.h)) else layer.points
                        points.zipWithNext().any { (a, b) -> distance(local, a, b) <= max((layer.stroke?.width ?: 0.0) / 2, tolerance) }
                    }
                }
                else -> box.containsPoint(local)
            }
            if (inside) return base.id
        }
        return null
    }
    return visit(layers, Affine.IDENTITY)
}

internal fun Rect.containsPoint(p: Point): Boolean = w >= 0 && h >= 0 && p.x >= x && p.x <= right && p.y >= y && p.y <= bottom

private fun square(n: Double): Double = n * n

private fun ellipseContains(r: Rect, p: Point): Boolean = r.w > 0 && r.h > 0 &&
    square((p.x - r.x - r.w / 2) / (r.w / 2)) + square((p.y - r.y - r.h / 2) / (r.h / 2)) <= 1

private fun distance(p: Point, a: Point, b: Point): Double {
    val dx = b.x - a.x
    val dy = b.y - a.y
    val length2 = dx * dx + dy * dy
    val t = if (length2 == 0.0) 0.0 else ((p.x - a.x) * dx + (p.y - a.y) * dy).div(length2).coerceIn(0.0, 1.0)
    return hypot(p.x - a.x - t * dx, p.y - a.y - t * dy)
}

// Invert the monotone quarter cubic instead of tessellating it: thin high-zoom corners retain their exact boundary.
private fun roundedContains(r: Rect, radius: Double, smoothing: Double, p: Point): Boolean {
    if (!r.containsPoint(p)) return false
    val reach = min(radius * (1 + smoothing), min(r.w, r.h) / 2)
    val x = min(p.x - r.x, r.right - p.x)
    val y = min(p.y - r.y, r.bottom - p.y)
    if (reach <= 0 || x >= reach || y >= reach) return true
    val control = reach * (1 - Paths.KAPPA) * (1 - .35 * smoothing)
    var low = 0.0
    var high = 1.0
    repeat(50) {
        val t = (low + high) / 2
        val s = 1 - t
        val cy = 3 * s * t * t * control + t * t * t * reach
        if (cy < y) low = t else high = t
    }
    val t = (low + high) / 2
    val s = 1 - t
    val boundary = s * s * s * reach + 3 * s * s * t * control
    return x + 1e-10 >= boundary
}
